import { trpc } from '../lib/trpc';
import { type ClewDatabase } from '../rxdb/database';
import { debug } from '../services/debug';
import { icons } from './Icons';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { Button, Group, Space, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';

type Props = {
  readonly db: Pick<ClewDatabase, 'processes'>;
  readonly modalId: string;
  readonly userId: string;
  readonly workspaceId: string;
};

const schema = z.object({
  title: z.string().min(1, 'Title is required'),
});

export const ProcessPropsForm = ({
  db,
  modalId,
  userId,
  workspaceId,
}: Props) => {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<z.infer<typeof schema>>({
    resolver: standardSchemaResolver(schema),
  });

  const { mutateAsync: createProcessAsync } =
    trpc.processes.create.useMutation();

  const submitHandler = handleSubmit(async (data) => {
    const subject = 'creating process';
    debug(subject, data);
    const response = await createProcessAsync({
      title: data.title,
      workspaceId,
    });
    if (response.ok) {
      debug(`success ${subject}`);
      const now = new Date().toISOString();
      await db.processes.upsert({
        archivedAt: null,
        createdAt: now,
        id: response.id,
        isPinned: false,
        snoozedUntil: null,
        title: data.title,
        updatedAt: now,
        userId,
        workspaceId,
      });
      modals.close(modalId);
    } else {
      debug(`error ${subject}`, response.error);
      showNotification({
        message: (
          <>
            <icons.Failure /> {response.error}
          </>
        ),
      });
    }
  });

  return (
    <form
      noValidate
      onSubmit={submitHandler}
    >
      <TextInput
        data-autofocus
        error={errors?.title?.message?.toString()}
        label="Title"
        tabIndex={0}
        {...register('title')}
      />
      <Space h="md" />
      <Group>
        <Button type="submit">Create</Button>
        <Button
          onClick={() => {
            modals.close(modalId);
          }}
          variant="outline"
        >
          Cancel
        </Button>
      </Group>
    </form>
  );
};
